import React, { useEffect, useState } from 'react'
import { X, Camera, Loader2 } from 'lucide-react'
import { toast } from 'react-toastify'
import { AdminSettings, useSaveSettingsMutation } from '../store/services/settingsApi'

interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
  settings?: AdminSettings | null
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, settings }) => {
  const [jobTitle, setJobTitle] = useState('')
  const [projectName, setProjectName] = useState('')
  const [interviewDate, setInterviewDate] = useState('')
  const [interviewTime, setInterviewTime] = useState('')
  const [hrManagerName, setHrManagerName] = useState('')
  const [adminName, setAdminName] = useState('')
  const [profilePic, setProfilePic] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [saveSettings, { isLoading }] = useSaveSettingsMutation()

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setJobTitle(settings?.jobTitle || '')
    setProjectName(settings?.projectName || '')
    setInterviewDate(settings?.interviewDate || '')
    setInterviewTime(settings?.interviewTime || '')
    setHrManagerName(settings?.hrManagerName || '')
    setAdminName(settings?.adminName || '')
    setProfilePic(null)
    setPreviewUrl(settings?.profilePic?.url || '')
  }, [isOpen, settings])

  useEffect(() => {
    return () => {
      if (previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  if (!isOpen) return null

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl)
    }

    setProfilePic(file)
    setPreviewUrl(URL.createObjectURL(file))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    const formData = new FormData()
    formData.append('jobTitle', jobTitle)
    formData.append('projectName', projectName)
    formData.append('interviewDate', interviewDate)
    formData.append('interviewTime', interviewTime)
    formData.append('hrManagerName', hrManagerName)
    formData.append('adminName', adminName)

    if (profilePic) {
      formData.append('profilePic', profilePic)
    }

    try {
      await saveSettings(formData).unwrap()
      toast.success('Settings saved successfully')
      onClose()
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to save settings')
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:items-center sm:p-4">
      <div className="my-4 flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-lg bg-white shadow-2xl sm:my-0">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2 sm:px-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
            <p className="text-sm text-gray-500">Update admin profile and project information</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-200 bg-white p-2 text-gray-500 transition hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex flex-col items-center justify-center gap-3 text-center">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-gray-100">
              {previewUrl ? (
                <img src={previewUrl} alt="Admin avatar" className="h-full w-full object-cover" />
              ) : (
                <Camera className="h-6 w-6 text-gray-400" />
              )}
            </div>
            <label className="inline-flex h-9 cursor-pointer items-center rounded-md border border-gray-300 px-4 text-sm font-medium text-gray-700 transition hover:bg-gray-50">
              Upload Profile Pic
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Admin Name</label>
            <input
              type="text"
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
              className="admin-filter-input py-3"
              placeholder="Enter admin name"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Job Title</label>
            <input
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              className="admin-filter-input py-3"
              placeholder="Enter job title"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Project Name</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="admin-filter-input py-3"
              placeholder="Enter project name"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Interview Date</label>
              <input
                type="date"
                value={interviewDate}
                onChange={(e) => setInterviewDate(e.target.value)}
                className="admin-filter-date py-3"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Interview Time</label>
              <input
                type="time"
                value={interviewTime}
                onChange={(e) => setInterviewTime(e.target.value)}
                className="admin-filter-input py-3"
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">HR Manager Name</label>
            <input
              type="text"
              value={hrManagerName}
              onChange={(e) => setHrManagerName(e.target.value)}
              className="admin-filter-input py-3"
              placeholder="Enter HR manager name"
            />
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center gap-2 rounded-md bg-[#c31200] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#a71000] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              <span>Save Settings</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SettingsModal
